/**
 * The accessibility module (§R, owner ask 2026-08-04: *"an accessibility module where things like
 * motion, font size multipliers, etc can be adjusted. WCAG compatible handling and stuff."*).
 *
 * Two halves live here and they are different kinds of thing:
 *
 * 1. **The three PREFERENCES the owner can set** - text scale, motion and contrast - applied to `<html>`,
 *    cached
 *    for the pre-paint stamp, reconciled with the workspace's stored answer at boot.
 * 2. **The modal focus manager** - focus into a dialog when it opens, contained while it is open,
 *    and back where it came from when it closes (WCAG 2.4.3 / 2.1.2).
 *
 * ## ⚠ This app has TWO audiences and they need OPPOSITE answers
 *
 * Everything in this file is for the OWNER, inside an app that can hold a preference. **A stranger on
 * a published page gets none of it, and that is the design, not an omission**: they have no app and no
 * settings, those pages are measured in bytes to first play, and a preferences widget on a smart link
 * is a control nobody came for sitting above the play button. Out there the answer is *be correct by
 * default and honor the platform preference* - which is what `linkPage.ts`/`bioPage.ts`/`epk.ts`
 * already do with their own hand-written `prefers-reduced-motion` rules. Do not import this module
 * into a published-page renderer; `tests/motion.test.ts` and `tests/a11y.test.ts` both fail that.
 *
 * ## Why a preference is applied as an INHERITED property and never as a class
 *
 * Round 148's rule, and the third time it has decided a design here (theme, motion, now this): **an
 * ambient condition reaches a shadow-rooted element ONLY through an inherited property.** Every island
 * and component in this app has been shadow since round 100, so a `:root.text-large` class or a
 * `.reduced-motion *` rule styles the sidebar and nothing else. What crosses is custom properties -
 * hence `--gh-text-scale` - and lengths that resolve against the document root - hence the `rem`
 * sweep that had to land before a text control could exist at all.
 *
 * `data-motion` on `<html>` looks like the exception and is not: the attribute is only a SELECTOR
 * HOOK for `public/style.css`'s two token blocks, and what actually reaches the elements is the
 * motion custom properties those blocks set. The attribute never has to cross a boundary.
 *
 * ## The pre-paint stamp, and why these are cached in localStorage
 *
 * Same argument as the theme's (see `public/index.html`): the answer lives server-side because it is
 * per-workspace, reaching it costs a round-trip, and by then the first frame is painted - at the
 * wrong text size, which is worse than the theme flash because the layout reflows under the reader.
 * So `index.html` stamps the cached values synchronously before the body paints and `initA11y()`
 * reconciles afterwards. ⚠ **The cache is a CACHE, never the source**: a workspace's answer is a row
 * in `settings`, so a second browser gets it right, and clearing site data loses nothing.
 */
import { api } from "./api.ts";
import {
	DEFAULT_CONTRAST,
	DEFAULT_MOTION,
	DEFAULT_TEXT_SCALE,
	isContrastPref,
	isMotionPref,
	isTextScale,
	type A11yPrefs,
	type ContrastPref,
	type MotionPref,
	type TextScale
} from "../../shared/a11y.ts";
import { refreshAccentTokens } from "./theme.ts";

/**
 * ⚠ These three keys are duplicated as string literals in `public/index.html`'s pre-paint script, which
 * is static and cannot import this module - the same bargain the theme's `theme-mode` key and the
 * wordmark make there. `tests/a11y.test.ts` asserts the two agree, so the stamp can never read a key
 * this module has stopped writing (which would fail SILENTLY: the app would simply look unscaled for
 * one frame, then correct itself, and nobody would file it).
 */
export const TEXT_SCALE_KEY = "a11y-text-scale";
export const MOTION_KEY = "a11y-motion";
export const CONTRAST_KEY = "a11y-contrast";

let current: A11yPrefs = { textScale: DEFAULT_TEXT_SCALE, motion: DEFAULT_MOTION, contrast: DEFAULT_CONTRAST };

/** What is live right now - the Settings card reads this to render its controls. */
export function a11yPrefs(): A11yPrefs {
	return { ...current };
}

/**
 * Put a text scale on screen and remember it for the next load's pre-paint stamp.
 *
 * The default is REMOVED rather than written as `1`, so an untouched install carries no inline style
 * at all and `public/style.css`'s `--gh-text-scale: 1` is what answers - the same "a stored row only
 * exists once somebody actively chose" rule the theme mode and the UI locale follow server-side.
 */
export function applyTextScale(scale: TextScale): void {
	const value = isTextScale(scale) ? scale : DEFAULT_TEXT_SCALE;
	current = { ...current, textScale: value };
	const root = document.documentElement;
	if (value === DEFAULT_TEXT_SCALE) root.style.removeProperty("--gh-text-scale");
	else root.style.setProperty("--gh-text-scale", String(value));
	try {
		if (value === DEFAULT_TEXT_SCALE) localStorage.removeItem(TEXT_SCALE_KEY);
		else localStorage.setItem(TEXT_SCALE_KEY, String(value));
	} catch {}
}

/** Stamp `<html data-motion>` (the hook `public/style.css`'s two token blocks select on). */
export function applyMotionPref(pref: MotionPref): void {
	const value = isMotionPref(pref) ? pref : DEFAULT_MOTION;
	current = { ...current, motion: value };
	const root = document.documentElement;
	if (value === DEFAULT_MOTION) delete root.dataset.motion;
	else root.dataset.motion = value;
	try {
		if (value === DEFAULT_MOTION) localStorage.removeItem(MOTION_KEY);
		else localStorage.setItem(MOTION_KEY, value);
	} catch {}
}

/**
 * Stamp `<html data-contrast>` and re-derive the accent's companions against the palette that just
 * arrived under them.
 *
 * ⚠ **The re-derive is not optional and it is easy to leave out.** `--accent-hover`, `--on-accent`
 * and `--accent-text` are computed from the accent the owner picked AGAINST THE LIVE SURFACES and
 * written inline (see `theme.ts`), so switching to a palette whose surfaces moved leaves all three
 * answering a question about the previous one - a link at 3:1 in the palette somebody turned on
 * BECAUSE they could not read the old one. `stamp()` does exactly this for a light/dark flip; this is
 * the same event wearing a different name.
 */
export function applyContrastPref(pref: ContrastPref): void {
	const value = isContrastPref(pref) ? pref : DEFAULT_CONTRAST;
	current = { ...current, contrast: value };
	const root = document.documentElement;
	if (value === DEFAULT_CONTRAST) delete root.dataset.contrast;
	else root.dataset.contrast = value === "high" ? "high" : "standard";
	try {
		if (value === DEFAULT_CONTRAST) localStorage.removeItem(CONTRAST_KEY);
		else localStorage.setItem(CONTRAST_KEY, value);
	} catch {}
	refreshAccentTokens();
}

/**
 * Reconcile the pre-paint stamp with the workspace's stored answer.
 *
 * Not awaited by anything at boot, unlike `initTheme()`: the cached stamp is already correct on every
 * load after the first, and the failure mode of a late arrival here is one frame at the previous
 * size, not a flash of the wrong palette. A failed read leaves the cache in place - never reset a
 * preference because a fetch lost.
 */
export async function initA11y(): Promise<void> {
	try {
		const prefs = await api<Partial<A11yPrefs>>("/api/settings/a11y");
		applyTextScale(isTextScale(prefs.textScale) ? prefs.textScale : DEFAULT_TEXT_SCALE);
		applyMotionPref(isMotionPref(prefs.motion) ? prefs.motion : DEFAULT_MOTION);
		applyContrastPref(isContrastPref(prefs.contrast) ? prefs.contrast : DEFAULT_CONTRAST);
	} catch {}
}

/** Persist the pair. The route stores only a non-default value, like the theme mode. */
export async function saveA11y(prefs: A11yPrefs): Promise<void> {
	await api("/api/settings/a11y", { method: "PUT", body: prefs });
}

// ── The modal focus manager (WCAG 2.4.3 keeping focus order, 2.1.2 no keyboard trap outside it) ──

/**
 * Everything a keyboard can land on. `:not([disabled])` and the negative-tabindex exclusion are what
 * make "the first focusable" mean the thing that will actually receive focus - a disabled Save button
 * as a dialog's first stop is a dead end that reads as the trap being broken.
 */
const FOCUSABLE =
	'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

/**
 * Collect what is focusable inside `root`, DESCENDING INTO SHADOW ROOTS.
 *
 * ⚠ The descent is the whole reason this is hand-rolled rather than one `querySelectorAll`. A modal in
 * this app is drawn by an island that is itself shadow, and half the controls inside it
 * (`<gh-select>`, `<gh-multiselect>`, `<gh-font-select>`) keep their focusable element inside a root
 * of their own. A flat query finds the custom-element HOSTS, which match nothing in the selector
 * above, so it returns the plain buttons and silently skips every real control - a trap that lets you
 * Tab straight past the form and out of the dialog while looking like it works.
 *
 * `offsetParent` is the visibility test, and it earns its keep on this app's `<details>` sections
 * (round 192/193): a collapsed section keeps its children in the DOM, and focusing something the
 * reader cannot see is the same bug as no trap at all.
 */
function focusablesIn(root: ParentNode): HTMLElement[] {
	const found: HTMLElement[] = [];
	const visit = (node: ParentNode): void => {
		for (const el of node.querySelectorAll<HTMLElement>("*")) {
			if (el.matches(FOCUSABLE) && (el.offsetParent != null || el === document.activeElement)) found.push(el);
			if (el.shadowRoot != null) visit(el.shadowRoot);
		}
	};
	visit(root);
	return found;
}

/**
 * `node.contains(el)` across shadow boundaries.
 *
 * ⚠ **`Node.contains` stops at a shadow root, and that broke the Queue's picker.** The follow-ups
 * modal opens with its `<gh-select>` focused, so the active element is inside THAT element's own
 * root - and `modal.contains(active)` is false for it, which made the "focus is already where it
 * should be" guard below think focus was outside the dialog and move it. Two existing UI tests failed
 * and named the picker, not this file. Walking up through `getRootNode().host` is the containment
 * question actually being asked, and it is the same crossing `composedPath()` does for events.
 */
function containsDeep(container: Node, node: Node | null): boolean {
	let current: Node | null = node;
	while (current != null) {
		if (container.contains(current)) return true;
		const root = current.getRootNode();
		current = root instanceof ShadowRoot ? root.host : null;
	}
	return false;
}

/** The deepest active element - `document.activeElement` stops at the outermost shadow HOST. */
function deepActiveElement(): HTMLElement | null {
	let el = document.activeElement;
	while (el?.shadowRoot?.activeElement != null) el = el.shadowRoot.activeElement;
	return el instanceof HTMLElement ? el : null;
}

/** The modal that currently owns the keyboard, the root that drew it, and where focus was before. */
let activeModal: HTMLElement | null = null;
let activeRoot: ParentNode | null = null;
let returnFocusTo: HTMLElement | null = null;
/** Which way the last Tab went - decides whether an escaped focus wraps to the first or the last. */
let backwards = false;

/**
 * Tell the manager which modal (if any) is open in `root`.
 *
 * ⚠ **Called from a reactive CONTROLLER on `GhElement`, deliberately not from `updated()`.** A modal
 * in this app is rendered from island state, so the only moment it can appear or disappear is a
 * render - but eleven islands already override `updated()` for their own imperative work, and a
 * twelfth that forgot `super.updated()` would be a page whose dialogs are silently untrapped, with
 * every gate green and nothing on screen looking wrong. Lit runs a controller's `hostUpdated()` from
 * its own internals, BEFORE and independently of any `updated()` override, so there is no cooperation
 * for a future island to forget. **When a hook has to fire for correctness rather than for a feature,
 * put it somewhere a subclass cannot skip it.**
 *
 * The three modals that are static chrome in `public/index.html` are handled by an attribute observer
 * in `initModalFocus()` instead - they are toggled by a `.hidden` class rather than by a render.
 */
export function syncModalFocus(root: ParentNode): void {
	const open = root.querySelector<HTMLElement>(".modal:not(.hidden)");
	if (open != null) {
		if (open !== activeModal) activateModal(open, root);
		return;
	}
	// ⚠ Only the root that DREW the active modal may retire it. Every island re-renders constantly,
	// and a sibling reporting "no modal here" would close a dialog that is open two islands away. The
	// `isConnected` half is the other direction and is the case a `root.contains()` test gets wrong:
	// closing a state-rendered modal DETACHES the node, so by the time this runs the owning root no
	// longer contains it either, and the trap would never be released.
	if (activeModal != null && (activeRoot === root || !activeModal.isConnected)) deactivateModal();
}

/**
 * Hold the page still behind an open dialog (owner, 2026-08-10: *"should not be able to scroll the
 * background behind a dialog when it's open. The shim/backdrop should block such scrolling"*).
 *
 * ⚠ **It lives HERE, beside the ARIA and the focus trap, for the reason those do**: it is a property
 * of *a dialog being open*, not of any one dialog, and this app draws them from twenty templates plus
 * three static ones in `index.html`. Put it in a modal's own open path and the twenty-first is born
 * without it, with every gate green. **A correctness behavior belongs where a new caller cannot
 * forget it.** The backdrop cannot do this on its own: `.modal` is a `position: fixed` overlay, and a
 * wheel event over a fixed element still scrolls the document behind it - CSS has no "swallow the
 * scroll" for that, which is why the ask reads as a bug about the backdrop and is fixed on `<body>`.
 *
 * ⚠ **The padding is not cosmetic.** Taking `overflow: hidden` removes the scrollbar, the viewport
 * gets ~15px wider, and the whole page - including the centred dialog - JUMPS sideways at the moment
 * it opens. Reserving exactly the width that disappeared is what makes the lock invisible. It is
 * measured rather than assumed because the number is 0 on overlay-scrollbar platforms (every Mac with
 * the default setting), where adding a fixed 15px would create the very jump it is meant to prevent.
 *
 * ⚠ **It is keyed on the none→one TRANSITION, not counted per call.** `syncModalFocus` calls
 * `activateModal` again when one dialog replaces another without a close in between, so a counter
 * would drift up by one on every swap and never unlock. The manager's model is one active modal at a
 * time and `activeModal` already carries it - the same test `returnFocusTo` makes one line below.
 */
let lockedPadding = "";
function lockBackgroundScroll(): void {
	if (document.body.classList.contains("modal-open")) return;
	const gap = window.innerWidth - document.documentElement.clientWidth;
	lockedPadding = document.body.style.paddingRight;
	if (gap > 0) document.body.style.paddingRight = `${gap}px`;
	document.body.classList.add("modal-open");
}

function unlockBackgroundScroll(): void {
	document.body.style.paddingRight = lockedPadding;
	document.body.classList.remove("modal-open");
}

function activateModal(modal: HTMLElement, root: ParentNode | null = null): void {
	// Remembered before anything moves, and remembered DEEPLY: the opener is very often a button
	// inside an island's shadow root, which plain `document.activeElement` reports as the island.
	if (activeModal == null) returnFocusTo = deepActiveElement();
	lockBackgroundScroll();
	activeModal = modal;
	activeRoot = root;

	// The dialog announces itself as one. ⚠ Set here rather than in twenty templates so a new modal
	// cannot be born without it - the failure of a missing `aria-modal` is that a screen reader keeps
	// reading the page BEHIND the dialog, which is invisible to anyone not using one.
	modal.setAttribute("role", "dialog");
	modal.setAttribute("aria-modal", "true");
	const content = modal.querySelector<HTMLElement>(".modal-content") ?? modal;
	// A heading inside the dialog IS its name; labelling it any other way duplicates a string that is
	// already on screen and lets the two drift.
	const heading = modal.querySelector<HTMLElement>("h2, h3, h4");
	if (heading != null) {
		if (heading.id === "") heading.id = `gh-modal-title-${Math.random().toString(36).slice(2, 9)}`;
		modal.setAttribute("aria-labelledby", heading.id);
	}

	// ⚠ Focus the CONTAINER, not the first field. A screen reader then reads the dialog's name and its
	// contents from the top; landing on the third input announces only that input, and the reader has
	// no idea what they are filling in. It also means a stray keystroke cannot type into a form the
	// owner has not looked at yet.
	content.tabIndex = -1;
	// After the render that opened it has committed - a node Lit created in this same tick is not
	// laid out yet, and `focus()` on an element with no box does nothing at all.
	requestAnimationFrame(() => {
		if (activeModal !== modal) return;
		// ⚠ **Never overrule a focus the OPENING code already placed inside this dialog.** The Queue's
		// follow-ups modal has exactly one thing to ask, so it opens with its `<gh-select>` focused and
		// its list already down - and this manager stealing that focus one frame later broke the
		// component's own Escape handling, because the key event stopped reaching the element that
		// listens for it. Two UI tests failed and named the picker, not the focus manager.
		//
		// The general form is worth keeping: **a correctness hook that runs after everything else must
		// check whether the thing it is about to fix is already right.** Focusing the container is the
		// answer for a dialog nobody has aimed at; it is a regression for one that was aimed.
		if (containsDeep(modal, deepActiveElement())) return;
		content.focus();
	});
}

function deactivateModal(): void {
	const restore = returnFocusTo;
	activeModal = null;
	activeRoot = null;
	returnFocusTo = null;
	unlockBackgroundScroll();
	// ⚠ Back where it came from (2.4.3). Without this, closing a dialog drops focus onto <body> and
	// the next Tab restarts at the top of the page - so a keyboard user who opens, edits and closes
	// three rows in a table walks the whole sidebar again three times.
	if (restore?.isConnected === true) restore.focus();
}

/**
 * Arm the manager. Called once from `app.ts`.
 *
 * ⚠ The listeners are on `document` and both of them depend on the event CROSSING a shadow boundary,
 * which `focusin` and `keydown` do (they are composed) - `tooltip.ts` learned the opposite lesson for
 * `mouseover`, whose composed path is truncated when both ends share a root. What does NOT survive the
 * crossing is `event.target`, which retargets to the outermost host, so both handlers below read
 * `composedPath()` instead. That is the same fix round 88 made and it is the general rule for any
 * document-level listener in this app.
 */
export function initModalFocus(): void {
	// ⚠ **There are no light-DOM dialogs left, so the static half of this manager is gone**
	// (2026-08-25). It used to walk `document.querySelectorAll(".modal")` at boot, put a
	// `MutationObserver` on each for the `.hidden` class only static markup ever carried, and stamp a
	// corner ✕ into dialogs Lit does not own. All three of those dialogs moved into islands, so every
	// line of it was watching for something that can no longer happen. **Every dialog in this app is
	// rendered by an island now, so `syncModalFocus` - the controller path on `GhElement` - is the
	// whole story**, and `tests/a11y.test.ts` keeps it that way by failing if a static one reappears.

	document.addEventListener("keydown", event => {
		if (event.key === "Tab") backwards = event.shiftKey;
	});

	armBackdropDismiss();

	// The trap itself, and it is deliberately a CORRECTION rather than a fence: let the browser move
	// focus wherever its own tab order says, and when that lands outside the open dialog, send it back
	// to the far end. Tabbing off the last control wraps to the first, Shift+Tab off the first wraps
	// to the last, and - the case a hand-built fence usually gets wrong - a click on the page behind
	// the overlay, or a browser-restored focus, is corrected too.
	document.addEventListener("focusin", event => {
		const modal = activeModal;
		if (modal == null) return;
		if (event.composedPath().includes(modal)) return;
		sendFocusBack(modal);
	});

	// ⚠ **The hole a `focusin`-only trap cannot see: focus that lands on NOTHING.** Tabbing off the
	// last control of the document does not move focus to another element - it leaves it on `<body>`,
	// and `focusout` is the only event that fires. So the correction above never ran, and the keyboard
	// user was left behind the overlay with the next Tab restarting at the top of the page. The same
	// state arrives a second way: a re-render that removes the focused control resets focus to the body
	// with no `focusin` either, which a modal rendered from island state can do at any moment.
	//
	// It cost a day to find because of how it FAILED - `tests/ui/a11y.test.ts` presses Tab 25 times and
	// asks where focus is, so whether the run met this state depended on which control the 25th press
	// landed on, which moves with the modal's focusable count. One run in the full suite failed; the
	// same file passed alone, twice. **A test that samples a cycle at a fixed offset reports a
	// deterministic bug as a flaky one.**
	//
	// Deliberately narrower than the handler above: it acts ONLY on the focus-went-nowhere case, never
	// on "focus is outside". `focusin` already owns every case where focus reached another element, and
	// a second handler with the same remit would fight it - notably when the dialog is CLOSING, where
	// `deactivateModal` moves focus to the opener on purpose.
	document.addEventListener("focusout", () => {
		const modal = activeModal;
		if (modal == null) return;
		// Deferred, because `focusout` fires BEFORE the incoming element takes focus: read now and every
		// ordinary Tab looks like an escape. A microtask is enough - the browser dispatches focusout and
		// focusin synchronously in one task - and it costs no frame, so the correction is invisible.
		queueMicrotask(() => {
			if (activeModal !== modal || !modal.isConnected) return;
			const active = deepActiveElement();
			if (active != null && active !== document.body) return;
			sendFocusBack(modal);
		});
	});
}

/**
 * Clicking the scrim closes the dialog (owner ask, 2026-08-13: *"Clicking outside a dismissable
 * dialog's scrim/backdrop should close it"*).
 *
 * ⚠ **It lives HERE, with the ARIA, the focus trap and the scroll lock, for the reason those do**: it
 * is a property of *a dialog being open* rather than of any one dialog, and this app draws them from
 * twenty templates plus three static ones in `index.html`. `modalClose.ts` already recorded that a
 * backdrop click worked *"on some of them"* - which is what a behavior implemented per-template
 * always decays into, because the twenty-first is born without it and every gate stays green.
 *
 * ⚠ **It CLICKS the dialog's own corner ✕ rather than dismissing anything itself**, which is the same
 * bargain `addStaticCloseMark` makes one level down, for the same reason: **the close ACTION is
 * per-modal and there is no generic version of it.** Some close by island state, three by stamping
 * `.hidden`, and the release editor and the quickstart wizard run a cancel path that frees a pending
 * upload - a hand-rolled dismissal here would be right eighteen times and silently leak an MP3 on the
 * nineteenth. The ✕ is already the app's one canonical exit and `tests/a11y.test.ts` gates its
 * presence on every dialog, so *what this gesture means* can never drift from *what the visible exit
 * means*. That is also the answer to "dismissable": a dialog is dismissable by the scrim exactly when
 * it offers a way out at all, which every dialog in this app does.
 *
 * ⚠ **A press that STARTED inside the dialog must not close it, and this is the half a naive
 * implementation gets wrong.** Selecting text in a long field and releasing the button past the edge
 * of the dialog fires `click` on the backdrop - so a plain click handler throws away the form of
 * anyone who drag-selects, which is precisely the reader who was working hardest in it. Both ends of
 * the gesture have to land on the scrim.
 */
function armBackdropDismiss(): void {
	// ⚠ `composedPath()[0]`, never `event.target`: a modal here is drawn inside an island's shadow
	// root, and target retargets to the outermost host - so every click in the app would look like it
	// landed on the island rather than on the scrim. The path's first entry is the true innermost
	// node, and the scrim IS the `.modal` element (its children share one centred grid cell), so
	// "landed on the modal itself" is exactly "landed outside the dialog".
	const onScrim = (event: Event, modal: HTMLElement): boolean => event.composedPath()[0] === modal;
	let pressedOnScrim = false;
	document.addEventListener("pointerdown", event => {
		pressedOnScrim = activeModal != null && onScrim(event, activeModal);
	});
	document.addEventListener("click", event => {
		const modal = activeModal;
		const pressed = pressedOnScrim;
		pressedOnScrim = false;
		if (modal == null || !pressed || !onScrim(event, modal)) return;
		// Every dialog has one (`modalClose()` in a template, `addStaticCloseMark` for the static
		// three) - but a missing mark must not make the scrim inert, so the fallback is the dismissal
		// the global Escape handler performs.
		const mark = modal.querySelector<HTMLElement>(".modal-x");
		if (mark != null) mark.click();
		else modal.classList.add("hidden");
	});
}

/**
 * Put focus back at the far end of the dialog - the first control when the last Tab went forwards, the
 * last when it went backwards, so a wrap reads as a wrap rather than as a jump.
 */
function sendFocusBack(modal: HTMLElement): void {
	const focusables = focusablesIn(modal);
	const target = backwards ? focusables[focusables.length - 1] : focusables[0];
	// No focusable content at all (a preview-only dialog): the container itself holds focus, which
	// is what `activateModal` gave a tabindex for. Never leave focus outside the dialog.
	(target ?? modal.querySelector<HTMLElement>(".modal-content") ?? modal).focus();
}
